import {
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import { TextField, Box } from '@mui/material';
import { InvitationInput, Role } from '../types/members-types';
import RolesAutocomplete from './RolesAutocomplete';
import ButtonGroup from '../../common/components/ButtonGroup';
import { setInviteButtonEnabled } from '../store-actions/invite-user-store-actions';

export interface InviteUserFormProps {
  onInvite: (invitationInput: InvitationInput) => void;
  onClose: () => void;
  availableRoles: Role[];
  isInviteEnabled: boolean;
  isInviteButtonLoading: boolean;
  hasErrorMessage: boolean;
  errorMessage: string;
}

const InviteUserForm: FunctionComponent<
  React.PropsWithChildren<InviteUserFormProps>
> = props => {
  const {
    onInvite,
    onClose,
    isInviteEnabled,
    availableRoles,
    isInviteButtonLoading,
    hasErrorMessage,
    errorMessage,
  } = props;
  const { classes } = useStyles();
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
  }, []);

  const handleOnChangeRole = useCallback(newRoles => {
    setSelectedRoles(newRoles);
  }, []);

  const handleOnInvite = useCallback(() => {
    const invitationInput: InvitationInput = {
      inviteeEmail: email,
      inviteeRoles: selectedRoles.map(role => role.id),
    };
    onInvite(invitationInput);
  }, [email, selectedRoles, onInvite]);

  useEffect(() => {
    setInviteButtonEnabled(email, selectedRoles);
  }, [email, selectedRoles]);

  return (
    <form className={classes.rootForm}>
      <Box display='flex' flexDirection='column' gap={3} mt={1}>
        <Box>
          <TextField
            required
            autoFocus
            type='email'
            variant='outlined'
            label='email'
            name='email'
            value={email}
            onChange={handleOnChange}
            error={hasErrorMessage}
            helperText={errorMessage}
            fullWidth
          />
        </Box>
        <Box mb={1}>
          <RolesAutocomplete
            availableRoles={availableRoles}
            selectedRoles={selectedRoles}
            handleOnChangeRole={handleOnChangeRole}
          />
        </Box>
        <ButtonGroup
          onCancel={onClose}
          onClick={handleOnInvite}
          isLoading={isInviteButtonLoading}
          buttonCancelLabel={'Cancel'}
          buttonCancelTooltip={'Cancel this operation'}
          buttonActionLabel={'Invite'}
          buttonActionTooltip={'Invite a team member'}
          buttonActionColor={'success'}
          isActionDisabled={!isInviteEnabled}
        />
      </Box>
    </form>
  );
};

const useStyles = makeStyles()(() => ({
  rootForm: {
    width: '100%',
  },
}));

export default InviteUserForm;
